import { CommonTooltips } from "@mcleod/common";
import { CallBackScheduler } from "@mcleod/common/src/CallBackScheduler";
import { Button, ChangeEvent, ClickEvent, Component, ContextMenuCreationEvent, Cursor, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceRemoteDataChangeEvent, Image, KeyEvent, Label, ModelDataChangeType, Panel, Snackbar, Table, TableColumn, TableRow, TableRowDisplayEvent, Textbox, Toast } from "@mcleod/components";
import { Alignment, Api, CompanySettings, DatePart, DateUtil, DisplayType, DisplayValue, LogManager,  ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { getDispatchControlBoolean, getDispatchControlValue, LMEUserSettings } from "@mcleod/general";
import { ModelDriverManagerProfile, RowDriverManagerProfile } from "../../../planning/src/models/ModelDriverManagerProfile";
import { AppointmentUtil } from "../../AppointmentUtil";
import { EquipmentAssignments } from "../../asset/EquipmentAssignments";
import { DriverEventSlideout } from "../../DriverEventSlideout";
import { ElectronicAdvances } from "../../ElectronicAdvances";
import { ManifestMovementQuickInfo } from "../../ManifestMovementQuickInfo";
import { MobileCommMessages } from "../../mobilecomm/MobileCommMessages";
import { WireUtil } from "../../WireUtil";
import { EditEquipment } from "../EditEquipment";
import { FilterPanel, SecondaryFilterProps } from "../FilterPanel";
import { OrderPlanningToolPanel, Tools } from "../OrderPlanningToolPanel";
import { PlanningUtil } from "../PlanningUtil";
import { AutogenLayoutDriverManagement } from "./autogen/AutogenLayoutDriverManagement";
import { DriverManagementExpand } from "./DriverManagementExpand";
import { DriverManagementSecondaryFilter } from "./DriverManagementSecondaryFilter";
import { DriverManagerFilter } from "./DriverManagerProfileFilter";
import { LateRouteViewSlideout } from "../../asset/LateRouteViewSlideout";

const log = LogManager.getLogger("lme.dispatch.planning.drivermgmt.DriverManagement");

export class DriverManagement extends AutogenLayoutDriverManagement {
    private timer: CallBackScheduler;
    private _profileRow: RowDriverManagerProfile;
    private driverManagerProfile: string;
    private filterObject: DriverManagerFilter;
    private filterPanel: FilterPanel;
    private enforceRegulatory: boolean;
    private advanceButtonVisible: boolean;
    private showingErrorToast: boolean;
    private handlingChanges: boolean = false;
    private changeEventList: DataSourceRemoteDataChangeEvent[] = [];
    private NEXT_STOP_ETA_FIELD = "next_stop_eta_time";
    private NEXT_STOP_ETA_TIMEZONE_FIELD = "next_stop_timezone";
    private dcMaxTrailers = getDispatchControlValue("maximum_trailers");

    public secondaryFilters: SecondaryFilterProps[] = [
        DriverManagementSecondaryFilter.FILTER_UNASSIGNED,
        DriverManagementSecondaryFilter.FILTER_PROJECTED_LATE,
        DriverManagementSecondaryFilter.FILTER_MC_MESSAGES,
        DriverManagementSecondaryFilter.FILTER_APPOINTMENT_LATE,
        DriverManagementSecondaryFilter.FILTER_ARRIVED_AT_SHIPPER,
        DriverManagementSecondaryFilter.FILTER_ARRIVED_AT_CONSIGNEE,
        DriverManagementSecondaryFilter.FILTER_UNSENT,
        DriverManagementSecondaryFilter.FILTER_UNACCEPTED,
        DriverManagementSecondaryFilter.FILTER_TEMP_WARNINGS,
        DriverManagementSecondaryFilter.FILTER_UNSEATED_DRIVER,
        DriverManagementSecondaryFilter.FILTER_FREE_TIME_STARTED,
        DriverManagementSecondaryFilter.FILTER_DETENTION_CHARGING,
        DriverManagementSecondaryFilter.FILTER_DETENTION_OCCURRED,
        DriverManagementSecondaryFilter.FILTER_DETENTION_CLOSED,
    ];

    override async onLoad() {
        this.enforceRegulatory = getDispatchControlBoolean("enforce_regulatory", true);
        this.filterPanel = new FilterPanel(this.secondaryFilters, this.tableDriverManagement, this.panelSecondaryFilters);
        this.filterPanel.syncFilterVisibility(null);
        await this.layoutOrderPlanningToolPanel.showButtons(Tools.DISPATCH, Tools.YSPLIT, Tools.VIEW_MOVEMENTS,
            Tools.ZMIT, Tools.SEAT_DRIVER, Tools.FEASIBILITY);
        this.setUpTable();
        PlanningUtil.showHideTrailerColumns(this.getTable())
        this.setUpMinuteTimer();
        const partialShipmentmentEnabled = CompanySettings.get().partial_shipment_enabled;
        const partialShipmentLicense = CompanySettings.get().partial_shipment_licensed;
        if (partialShipmentmentEnabled === false || partialShipmentLicense === false) {
            const column: TableColumn = this.tableDriverManagement.columns.find(col => "columnHeaderO" === col.headingCell.id);
            if (column) {
                column.cellDef.def.components[1].excludeFromSortFields = ["manifest_id"];
            }
            this.tableDriverManagement.resetColumnSorting();
        }
        await this.setupAdvancesButton();
    }

    private setUpTable() {
        const table = this.getTable();
        table.overrideUrlParams = (selectedRow: TableRow) => {
            return {key: selectedRow.data.get("movement_id"), orderId: selectedRow.data.get("order_id", "none")}
        };
        table.allowDetailForRow = (row: TableRow): boolean => {
            if ((row.data as ModelRow).isNull("movement_id")) {
                Snackbar.showWarningSnackbar("No current movement associated with this driver");
                return false;
            }
            return true;
        };
        table.determineDetailKeyValue = (tableRow) => tableRow.data.get("movement_id");
    }

    getToolsPanel(): OrderPlanningToolPanel {
        return this.layoutOrderPlanningToolPanel.toolPanel;
    }

    // START - Event handlers
    tableDriverManagementAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) return;
        this.sourceDriverManager.data.forEach(row => PlanningUtil.calculateReeferTempStatus(row));
    }

    tableDriverManagementOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        tableRow.contextMenuItems = () => this.getToolsPanel().getContextMenuItems(tableRow);
        tableRow.addContextMenuCreationListener((event: ContextMenuCreationEvent) => {
            this.getToolsPanel().contextMenuCreationEvent(event);
        });

        this.minuteRowUpdater(tableRow);
        this.updateNextStopBadges(tableRow);
        this.setUpDriverComments(tableRow);
        this.setUpOrderIdLabel(tableRow, modelRow);
        this.setupNextStopWindow(tableRow, modelRow);
        PlanningUtil.checkScheduledLate(tableRow, "next_stop")
        this.setUpDutyStatus(tableRow, modelRow);
        this.setUpServiceIncident(tableRow, modelRow);
        this.setUpPreassignmentTooltipContent(tableRow);
        PlanningUtil.setUpMcMessages(tableRow, modelRow);
        PlanningUtil.setEquipIcons(tableRow, modelRow, this.enforceRegulatory, false);
        PlanningUtil.setDroppedTrailerFont(tableRow, modelRow, this.dcMaxTrailers);
        AppointmentUtil.setAppointmentImageForTableRow(tableRow, "imageNextStopAppointment", "next_stop_appt_required", "next_stop_appt_confirmed");
        this.setupYsplits(modelRow, tableRow);
        this.setupDriverAdvances(tableRow);
        this.setUpTimeSinceDelivered(tableRow, modelRow);
        this.setupZmitIcons(modelRow, tableRow);
        this.setupDriverAlerts(tableRow, modelRow);
        this.setupDriverEvents(tableRow, modelRow);
        PlanningUtil.setupNextStopLateStatusPanel(tableRow);
        this.setupTracking(tableRow, modelRow);
        this.setReeferIcon(tableRow, modelRow);
        PlanningUtil.setPlanningCommentsIcon(tableRow, modelRow, false);
        PlanningUtil.setupDriverStatus(tableRow, modelRow);
        PlanningUtil.setupDriverPta(tableRow, 1);
        PlanningUtil.displayDetentionIcon(tableRow);
        tableRow.forEveryChildComponent(comp => {
            PlanningUtil.setPlanningTooltipContent(comp, modelRow);
        });
        if (!modelRow.isNull("manifest_id")) {
            const orderId = tableRow.findComponentById("labelOrderId") as Label;
            orderId.visible = false;
            const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
            if (manifestIdLabel != null) {
                manifestIdLabel.visible = true;
                const manifestId = modelRow.get("manifest_id");
                const movementId = modelRow.get("movement_id");
                manifestIdLabel.addUnderlineWhenMouseOver();
                ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(manifestIdLabel, movementId, manifestId);
            }
        }
        this.determineOrderNumberVisibility(tableRow, modelRow);
    }

    public determineOrderNumberVisibility(tableRow: TableRow, modelRow: ModelRow) {
        const panelIds = tableRow.findComponentById("panelIds") as Panel;
        const panelEmptyOrder = tableRow.findComponentById("panelEmptyOrder") as Panel;
        const hasManifestId = !StringUtil.isEmptyString(modelRow.get("manifest_id"));
        const hasOrderId = !StringUtil.isEmptyString(modelRow.get("order_id"));
        const showIdsPanel = hasManifestId || hasOrderId;
        panelIds?.setProps({ visible: showIdsPanel });
        panelEmptyOrder?.setProps({ visible: !showIdsPanel });
    }

    sourceDriverManagerOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (this.handlingChanges) {
            this.changeEventList.push(event);
            return
        }
        this.handleRemoteDataChange(event);
    }

    sourceDriverManagerOnSubscriberError(event) {
        log.error(event.error);
        if (!this.showingErrorToast) {
            this.showingErrorToast = true;
            Toast.showToast("There was a problem keeping this page up-to-date real time.  Try refreshing the page.", null, { persist: true, onDismiss: () => this.showingErrorToast = false });
        }
    }

    async buttonDriverAdvancesOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(button);
        const modelRow = tableRow.data as ModelRow;
        const movementBrokerage = false;
        const validatedFrom = "Master Driver";

        const driverId = modelRow.getString("driver1_id");
        const driverName = modelRow.getString("driver1_name");

        // Check if we have an order_id in the table row data
        const moveStatus = modelRow.getString("move_status");
        let orderId: string;
        let movementId: string;
        let moveType: string;
        let manifestId: string;

        if (moveStatus != "P" && modelRow.getNumber("driver1_preassign_count") > 0) {
            orderId = modelRow.getString("np_order_id");
            movementId = modelRow.getString("np_move_id");
            manifestId = movementId ? await WireUtil.getManifestIdbyMovementId(movementId) : null;
            moveType = "A";
        } else {
            orderId = modelRow.getString("order_id");
            movementId = modelRow.getString("movement_id");
            manifestId = modelRow.getString("manifest_id");
            moveType = moveStatus === "P" ? "P" : "D";
        }

        // If no order_id exists (e.g., locate move), check driver's latest assignments
        if (!orderId) {
            const response = await WireUtil.driverOrderAndMovements(driverId);
            orderId = response?.order_id;
            movementId = response?.movement_id;
            manifestId = movementId ? await WireUtil.getManifestIdbyMovementId(movementId) : null;
            moveType = response?.move_type;
        }

        const options = {
            movementId,
            movementBrokerage,
            orderId,
            driverId,
            driverName,
            validatedFrom,
            moveType,
            manifestId
        };
        ElectronicAdvances.showSlider(options);
    }

    tableDriverManagementOnRowExpand(event) {
        const tableRow = event.getTableRow() as TableRow;
        const data = tableRow.data as ModelRow;
        if (data != null) {
            const layout = event.expandComponents[0] as DriverManagementExpand;
            layout.addLayoutLoadListener(() => {
                layout.layoutDriverManagementEventsTable.driverId = data.get("driver1_id");
                layout.layoutDriverManagementEventsTable.driverName = data.get("driver1_name");
                layout.layoutDriverManagementEventsTable.driverMgrId = data.get("driver1_manager");
                layout.layoutDriverManagementEventsTable.driverMgrName = data.get("driver1_manager_name");
                layout.sourceDriverEvents.search({
                    equipment_id: data.get("driver1_id"),
                    equipment_type_id: "D",
                    event_status: "in A,U"
                });

                const tractorId = data.get("tractor");
                layout.sourceEquipmentPositionsAndCallins.search({
                    tractor_id: tractorId,
                    movement_id: data.get("movement_id"),
                    last_stop_equipment: tractorId,
                    callin_id: tractorId
                });

                layout.sourceDriverManager.setRowsAndMode(DataSourceMode.NONE, [data]);
                layout.refreshEmptyMoveState();
                layout.setupTemp();
                layout.displayArriveLateTime();
                layout.setUpStopsTextbox();
                layout.setTooltipContent();
            });
        }
    }

    orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelEmptyOrderOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Label);
        const movementId = tableRow?.data?.get("movement_id");
        if (movementId)
            PlanningUtil.navigateToMovement(movementId, tableRow?.data?.get("order_id", null));
    }

    private imageCommentsOnClick(event: ClickEvent) {
        const image = event.target as Component;
        if (!image) return;

        const tableRow = TableRow.getContainingTableRow(image);
        if (tableRow == null) return;

        const modelRow = tableRow.data as ModelRow;
        EditEquipment.showSlideout({
            driverName: modelRow.get("driver1_name"),
            driverId: modelRow.get("driver1_id"),
            tractorId: modelRow.get("tractor"),
            trailerId: modelRow.get("trailer"),
            trailer2Id: modelRow.get("trailer2"),
            trailer3Id: modelRow.get("trailer3"),
            orderId: modelRow.get("order_id"),
            hideTeamDriver: true
        });
    }

    buttonMcMessagesOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const modelRow = tableRow.data as ModelRow;
        MobileCommMessages.openSlideout({
            movementId: modelRow.get("movement_id"),
            tractorId: modelRow.get("tractor"),
            selectedDriverId: modelRow.get("driver1_id"),
            selectedDriverName: modelRow.get("driver1_name")
        });
    }

    labelPreassignOnClick(event: ClickEvent) {
        const modelRow = TableRow.getContainingTableRow(event.target as Label).data as ModelRow;
        if (modelRow == null)
            return;

        const delivered = "D" === modelRow.get("move_status", "");
        const moveId = modelRow.get(delivered ? "np_move_id" : "movement_id");

        const button = event.target as Button;
        button.busy = true;
        EquipmentAssignments.showSlideout({
            equipmentId: modelRow.get("driver1_id"),
            equipmentType: "D",
            tractorId: modelRow.get("tractor", null),
            movementId: moveId
        });
        button.busy = false;
    }

    panelDriverEventOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Panel);
        const modelRow = tableRow.data as ModelRow;
        if (!modelRow.isNull("driver1_closest_event")) {
            this.showDriverEventSlideoutFromTableRow(event);
        }
    }

    panelHoSOnClick(event: ClickEvent) {
        PlanningUtil.showHoursOfServiceSlideout(event, "driver1_id");
    }

    detentionOnClick(event: ClickEvent) {
        const modelRow = TableRow.getContainingTableRow(event.target as Component)?.data as ModelRow;
        let orderId = null;
        if (modelRow && !modelRow.isNull("next_stop_order_id")) {
            orderId = modelRow.get("next_stop_order_id");
        }
        PlanningUtil.showMultiStopSlideout(event.target as Image, true, orderId);
    }

    async labelNextStopLateStatusOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data as ModelRow;
        let moveId: string;
        if(modelRow.get("move_status") === "D") {
            moveId = modelRow.get("np_move_id");
        }
        else {
            moveId = modelRow.get("movement_id");
        }
        const response = await Api.search("lme/dispatch/planning/late-route", {
            "movement_id": moveId,
            "is_current": "Y"
        });
        new LateRouteViewSlideout({
            movementId: moveId,
            stopId: modelRow.get("next_stop_stop_id"),
            isCurrent: response.actual_row_count > 0
        });
    }

    async labelNextStopLateStatusChevronOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data as ModelRow;
        const panel = tableRow.findComponentById("panelNextStopLateStatus") as Panel;
        const stopId = modelRow.get("next_stop_stop_id");
        let moveId: string;
        if (modelRow.get("move_status") === "D") {
            moveId = modelRow.get("np_move_id");
        } else {
            moveId = modelRow.get("movement_id");
        }
        await PlanningUtil.labelNextStopLateStatusChevronOnClick(event, panel, stopId, moveId);
    }

        private displayLateRouteStatusDropDowns(panel: Panel) {
        return PlanningUtil.displayLateRouteStatusDropDowns(panel);
    }

    private async updateLateRouteStatus(panel: Panel, movementId: string, stopId: string, statusRow: ModelRow) {
        return await PlanningUtil.updateLateRouteStatus(panel, movementId, stopId, statusRow);
    }
    // END - Event handlers

    // START - Minute updater stuff
    private setUpMinuteTimer() {
        log.debug("Starting minute timer");
        this.timer = new CallBackScheduler(this, () => this.executeMinuteUpdates, DatePart.MINUTE, true);
        this.addUnmountListener(() => this.clearMinuteTimer());
    }

    private clearMinuteTimer() {
        log.debug("Clearing minute timer");
        this.timer.stopTimer();
    }

    private executeMinuteUpdates() {
        log.debug("Executing minute updates");
        for (const tableRow of this.tableDriverManagement.rows) {
            if (!tableRow.populatedDOM) {
                return;
            }
            this.minuteRowUpdater(tableRow);
        }
    }

    // Add calls that need to be run every minute. This will be for things that may need to update the UI even though
    // the data has not changed.  Essentially, your function should be comparing something against the current time.
    // Most things will need to be updated when the data changes and should be added to the onRowDisplay event instead.
    private minuteRowUpdater(tableRow: TableRow) {
        // exampleFunction(tableRow);
    }
    // END - Minute updater stuff

    // START - Planning Comment stuff
    textboxPlanningCommentOnChange(event: ChangeEvent) {
        PlanningUtil.textboxPlanningCommentOnChangeListener(event);
    }

    private setUpDriverComments(tableRow: TableRow) {
        const data = tableRow.data as ModelRow;
        const textbox = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        const onSave = async () => {
            const driverId: string = data.get("driver1_id");
            await Api.post("lme/dispatch/update-driver-planning-comment",
                {driver_id: driverId, planning_comment: textbox.text}
            );
        }

        PlanningUtil.addTextboxEditability({
            textbox:textbox,
            idText: "PlanningComment",
            tableRow: tableRow,
            useWrapper: true,
            onSave:onSave
        });

        if (textbox != null && textbox.getListeners("_keyDownListeners") == null) {
            const saveButton = tableRow.findComponentById("buttonSavePlanningComment") as Button;
            textbox.addKeyDownListener((event: KeyEvent) => PlanningUtil.savePlanningCommentOnKeyDown(event, saveButton));
        }
    }

    // END - Planning Comment stuff

    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile");
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? this.driverManagerProfile ?? LMEUserSettings.get()?.driver_manager_profile;
        if (profileId) {
            const profileRow = await new ModelDriverManagerProfile().searchSingle({ id: profileId });
            if (profileRow) {
                log.debug("Driver Manager Profile", profileRow);
                this.setProfileRow(profileRow);
            } else {
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            }
        } else {
            Snackbar.showSnackbar("You do not have a default driver manager profile defined. "
                + "Add a default profile to your preferences so your drivers will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        }
    }

    get tableMovements() {
        return this.tableDriverManagement;
    }

    public getTable(): Table {
        return this.tableDriverManagement;
    }

    public get profileRow(): RowDriverManagerProfile {
        return this._profileRow;
    }

    public setProfileRow(value: ModelRow) {
        if (value != this.profileRow) {
            this._profileRow = value;
            this.filterObject = new DriverManagerFilter(value);
            if (this.profileRow) {
                this.searchDataSources();
            }
        }
    }

    override get windowTitle(): string {
        const title = "Driver Management";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    private async searchDataSources() {
        this.handlingChanges = true;
        this.toggleTableBusy(true);

        await this.searchEquipMoves();
        this.toggleTableBusy(false);

        if (this.changeEventList.length > 0)
            this.handleRemoteDataChange(this.changeEventList.shift());
        else
            this.handlingChanges = false;
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const source = event.target as DataSource;
        const changedRow = event.change.data as ModelRow;
        const index = source.findRowInData(changedRow);
        const currentRow = index >= 0 ? source.data[index] : null;

        if (currentRow != null && !currentRow.isNull("lastUpdateNanos")
            && currentRow.get("lastUpdateNanos") > event.change.data.get("lastUpdateNanos")) {
            event.preventDefault();
            return;
        }

        if (event.change.type !== ModelDataChangeType.DELETE && !this.filterObject.dataPasses(event.change.data)) {
            event.change.type = ModelDataChangeType.DELETE;
        }

        if (event.change.type === ModelDataChangeType.ADD && index >= 0) {
            event.change.type = ModelDataChangeType.UPDATE;
        }

        const table = this.getTable();
        PlanningUtil.calculateReeferTempStatus(changedRow);
        PlanningUtil.syncOrderPlanningComments(currentRow, source, table);

        if (this.changeEventList.length > 0)
            this.handleRemoteDataChange(this.changeEventList.shift());
        else
            this.handlingChanges = false;
    }

    private async searchEquipMoves() {
        await this.sourceDriverManager.search({ profile_row: this.profileRow });
        this.sourceDriverManager.setRowsAndMode(DataSourceMode.NONE, this.sourceDriverManager.data, null);
    }

    private toggleTableBusy(busy: boolean) {
        this.tableDriverManagement.busy = busy;
    }

    async setupDriverAdvances(tableRow: TableRow) {
        const driverAdvancesButton = tableRow.findComponentById("buttonDriverAdvances") as Button;

        if (!this.advanceButtonVisible) {
            driverAdvancesButton.visible = false;
            return;
        }

        const modelRow = tableRow.data as ModelRow;
        const driverId = modelRow.getString("driver1_id");

        // Extract button setup to separate method
        this.configureAdvanceButton(driverAdvancesButton, modelRow);

        // Extract color logic to separate method
        if (driverAdvancesButton.enabled && driverAdvancesButton.tooltip === "Issue Driver Advance") {
            // Check if we have an order_id in the table row data
            const moveStatus = modelRow.getString("move_status");
            let orderId: string;

            if (moveStatus != "P" && modelRow.getNumber("driver1_preassign_count") > 0) {
                orderId = modelRow.getString("np_order_id");
            } else {
                orderId = modelRow.getString("order_id");
            }

            // If no order_id exists (e.g., locate move), get latest assigned order from backend
            if (!orderId) {
                const response = await WireUtil.driverOrderAndMovements(driverId);
                orderId = response?.order_id;
            }

            this.setupAdvanceButtonInteractions(driverAdvancesButton, { driverId, orderId });
        }
    }

    private configureAdvanceButton(button: Button, modelRow: ModelRow) {
        const iconEnable = modelRow.getBoolean("driver1_advance_enabled", false);
        const iconVisible = modelRow.getBoolean("driver1_advance_visible", false);
        const tooltipMessage = modelRow.getString("driver1_advance_tooltip", null);

        button.cursor = iconEnable ? Cursor.POINTER : null;
        button.visible = iconVisible;
        button.enabled = iconEnable;
        button.tooltip = tooltipMessage;
    }

    private setupAdvanceButtonInteractions(button: Button, advanceProps: any) {
        const { driverId, orderId } = advanceProps;
        const byPayeeLastSevenDays = true;

        // Setup hover to discover
        WireUtil.setDetailHoverToDiscover(button, orderId, driverId, byPayeeLastSevenDays, "Other", null);

        // Setup color logic
        this.updateAdvanceButtonColor(button, driverId, orderId);
    }

    private async updateAdvanceButtonColor(button: Button, driverId: string, orderId: string) {
        try {
            const hasHistory = await this.checkDriverAdvanceHistory(driverId, orderId);
            const tableRow = TableRow.getContainingTableRow(button);
            const modelRow = tableRow.data as ModelRow;
            const showIconWithCheck = modelRow.getBoolean("show_icon_with_check", false);

            if (hasHistory && !showIconWithCheck) {
                button.color = "success";
            } else {
                button.color = "primary";
            }
        } catch (error) {
            log.error("Error checking driver advance history:", error);
            button.color = "default.lightest";
        }
    }

    private async setupAdvancesButton () {
        if (WireUtil.isUserDeniedAccessToLayout()) {
            this.advanceButtonVisible = false;
            return;
        }

        const responsePermission = await WireUtil.userPermissionForWireCode(false);
        this.advanceButtonVisible = responsePermission?.visible_driver_advances;
    }

    private updateNextStopBadges(tableRow: TableRow) {
        const badgeNextArrived = tableRow?.findComponentById("badgeNextArrived") as Label;
        const badgeNextDeparted = tableRow?.findComponentById("badgeNextDeparted") as Label;
        const modelRow = tableRow.data as ModelRow;
        badgeNextDeparted.visible = !modelRow?.isNull("next_stop_actual_departure");
        badgeNextArrived.visible = !modelRow?.isNull("next_stop_actual_arrival");
    }

    private setReeferIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imageReefer = tableRow.findComponentById("imageReefer") as Image;
        PlanningUtil.setReeferIcon(imageReefer, modelRow);
    }

    private setUpOrderIdLabel(tableRow: TableRow, modelRow: ModelRow) {
        const isEmpty = modelRow.getBoolean("emptied", false);
        const imageLoaded = tableRow.findComponentById("imageLoaded") as Image;
        imageLoaded?.setProps({ visible: !isEmpty && !modelRow.isNull("movement_id") });
        if (isEmpty) {
            const emptyOrderLabel = tableRow.findComponentById("labelEmptyOrder") as Label;
            if (modelRow.isNull("movement_id")) {
                emptyOrderLabel.text = "--"
                const imageEmptyOrder = tableRow.findComponentById("imageEmptyOrder") as Image;
                imageEmptyOrder?.setProps({ visible: false });
            } else {
                emptyOrderLabel?.addUnderlineWhenMouseOver();
            }
        } else {
            const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
            orderIdLabel.addUnderlineWhenMouseOver();
            orderIdLabel.addClickListener((event: ClickEvent) => {
                this.orderNumberOnClick(event);
            });
        }
    }

    private setupNextStopWindow(tableRow: TableRow, modelRow: ModelRow) {
        const nextStopEarly: Date = modelRow.getDateTime("next_stop_early", null);
        const nextStopLate: Date = modelRow.getDateTime("next_stop_late", null);
        const nextStopEarlyF: string = DateUtil.formatDateTime(nextStopEarly, DateUtil.getUserDateTimeFormat());
        const nextStopLateF: string = DateUtil.formatDateTime(nextStopLate, DateUtil.getUserDateTimeFormat());
        const timezone: string = modelRow.getString("next_stop_timezone", "");
        let nextStopWindow: string = "";

        if (nextStopEarly && nextStopLate) {
            if (nextStopEarly.toDateString() === nextStopLate.toDateString()) {
                const lateTime = DateUtil.formatTime(nextStopLate, DateUtil.getUserTimeFormat());
                nextStopWindow = `${nextStopEarlyF} - ${lateTime} ${timezone}`;
            } else {
                nextStopWindow = `${nextStopEarlyF} - ${nextStopLateF} ${timezone}`;
            }
        } else if (nextStopEarly) {
            nextStopWindow = `${nextStopEarlyF} ${timezone}`;
        } else if (nextStopLate) {
            nextStopWindow = `${nextStopLateF} ${timezone}`;
        }

        const nextStopWindowLabel = tableRow.findComponentById("labelNextWindow") as Label;
        nextStopWindowLabel.text = nextStopWindow;
    }

    private setUpDutyStatus(tableRow: TableRow, modelRow: ModelRow) {
        const dutyStatus = tableRow.findComponentById("textboxDriverDutyStatus") as Textbox;
        if (dutyStatus && !modelRow.isNull("driver1_id")) {
            CommonTooltips.setTooltipContentLayout(dutyStatus,
                modelRow.getString("driver1_id"), "lme/dispatch/asset/DriverHosQuickInfo");
        }
    }

    private setUpServiceIncident(tableRow: TableRow, modelRow: ModelRow) {
        const imageServiceFail = tableRow.findComponentById("imageServiceIncidents") as Image;
        if (imageServiceFail != null) {
            CommonTooltips.setTooltipContentLayout(imageServiceFail, null, "lme/dispatch/ServiceIncidentQuickInfo", { maxWidth: 800, maxHeight: 600 }, { movement_id: modelRow.get("movement_id"), order_id: modelRow.get("order_id"), "status": "A" });
        }
    }

    private setUpPreassignmentTooltipContent(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const labelPreassignCount = tableRow.findComponentById("labelPreassignCount");
        const preassignCount = modelRow.get("driver1_preassign_count") as number;
        if (preassignCount > 0) {
            CommonTooltips.setTooltipContentLayout(
                labelPreassignCount, null, "lme/dispatch/planning/PreassignOrderQuickInfoTable", null,
                {"equipment_type": "D", "equipment_id": modelRow.get("driver1_id")}
            );
            labelPreassignCount.tooltipDynamicOptions = { position: Alignment.LEFT };
        }
    }



    private setupYsplits(modelRow: ModelRow, tableRow: TableRow) {
        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        if (originStopType == "SP" || destStopType == "SD") {
            const imageId = originStopType === "SP" && destStopType === "SD" ? "imageYsplitComplete" : originStopType === "SP" ? "imageYsplitPick" : destStopType === "SD" ? "imageYsplit" : null;
            const imageYsplit = tableRow.findComponentById(imageId) as Image;
            imageYsplit.visible = true;
        }
    }

    private setUpTimeSinceDelivered(tableRow: TableRow, modelRow: ModelRow) {
        const textboxTimeSinceDelivered = tableRow.findComponentById("textboxTimeSinceDelivered") as Label;
        if (modelRow.getNumber("driver1_preassign_count")) {
            textboxTimeSinceDelivered.visible = false;
        }
        else {
            const hoursSinceDelivered: number = modelRow.get("time_since_delivered", 0);
            PlanningUtil.configureTimeSinceDelivered(textboxTimeSinceDelivered, hoursSinceDelivered);
        }
    }

    private setupZmitIcons(modelRow: ModelRow, tableRow: TableRow) {
        const xmitted2driver = modelRow.getBoolean("np_xmitted", false);
        const xmitAccepted = modelRow.get("np_acknowledged");

        let image = tableRow.findComponentById("imageZmitTransmitted");
        image.visible = xmitted2driver && xmitAccepted == null;
        image = tableRow.findComponentById("imageZmitAccepted");
        image.visible = xmitted2driver && xmitAccepted === 'Y';
        image = tableRow.findComponentById("imageZmitRejected");
        image.visible = xmitted2driver && xmitAccepted === 'N';
    }

    private setupDriverAlerts(tableRow: TableRow, modelRow: ModelRow) {
        // Driver 1
        PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageD1Alert", "driver1_msg_count",
            "driver1_msg_urgent", { driverId: modelRow.get("driver1_id") });

        // Tractor and Trailer 1
        PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT1Alert", "t1_msg_count",
            "t1_msg_urgent", { tractorId: modelRow.get("tractor"), trailerId: modelRow.get("trailer") });

        if (this.dcMaxTrailers >= 2) {
            // Trailer 2
            PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT2Alert", "trailer2_msg_count",
                "trailer2_msg_urgent", { trailerId: modelRow.get("trailer2") });
            if (this.dcMaxTrailers >= 3) {
                // Trailer 3
                PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT3Alert", "trailer3_msg_count",
                    "trailer3_msg_urgent", { trailerId: modelRow.get("trailer3") });
            }
        }
    }

    private setupDriverEvents(tableRow: TableRow, modelRow: ModelRow) {
        const imageDriverEvents = tableRow?.findComponentById("imageDriverManagementEvents") as Image;
        imageDriverEvents.visible = !modelRow?.isNull("driver1_closest_event");
        if (!imageDriverEvents.visible) {
            const panelDriverEvents = tableRow?.findComponentById("panelDriverManagementEvents") as Panel;
            panelDriverEvents.paddingLeft = 0;
        }

        if (!modelRow?.isNull("driver1_next_home")) {
            const nextHomeDate = modelRow?.getDate("driver1_next_home");
            const today = new Date();
            const diff = nextHomeDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
            const labelDaysOut = tableRow?.findComponentById("labelDaysOut") as Label;
            labelDaysOut.text = diffDays >= -30 ? StringUtil.pluralString(diffDays, "Day Out", "Days Out") : "--";
        }
    }

    private setupTracking(tableRow: TableRow, modelRow: ModelRow) {
        const labelETAToNextStop = tableRow.findComponentById("labelETAToNextStop") as Label;
        if (labelETAToNextStop != null) {
            labelETAToNextStop.visible = !modelRow.isNull(this.NEXT_STOP_ETA_FIELD);
        }

        const textboxLastCallinEta = tableRow.findComponentById("textboxLastCallinEta") as Textbox;
        if (textboxLastCallinEta != null && textboxLastCallinEta.hasText() && modelRow.get(this.NEXT_STOP_ETA_TIMEZONE_FIELD) != null) {
            textboxLastCallinEta.text = DisplayValue.getDisplayValue(modelRow.getDateTime(this.NEXT_STOP_ETA_FIELD), DisplayType.DATETIME) + " " + modelRow.get(this.NEXT_STOP_ETA_TIMEZONE_FIELD);
        }

        const driverLastLocation = tableRow.findComponentById("textboxDriverLastLocation") as Label;
        CommonTooltips.setTooltipContentLayout(driverLastLocation, modelRow.get("driver1_id"), "lme/dispatch/asset/DriverLastLocationQuickInfo");
    }

    private showDriverEventSlideoutFromTableRow(event: ClickEvent) {
        const slideout = new DriverEventSlideout();
        const data = TableRow.getContainingTableRow(event.target as Image)?.data as ModelRow;

        slideout.showSlideout({
            equipmentId: data.getString("driver1_id"),
            equipmentName: data?.getString("driver1_name"),
            driverMgrId: data?.getString("driver1_manager"),
            driverMgrName: data?.getString("driver1_manager_name"),
            equipStatusId: data.getString("driver1_closest_event_id")
        });
    }

    /** This is an event handler for the onClick event of labelManifestId. */
    labelManifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Label);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    private async checkDriverAdvanceHistory(driverId: string, orderId: string): Promise<boolean> {
        try {
            // Call the same API that WireUtil.setDetailHoverToDiscover uses
            const response = await Api.search("lme/dispatch/wire-advance-quick-info", {
                "order_id": orderId,
                "driver_id": driverId,
                "last_seven_days": true,
                "history_from": "Other",
                "movement_id": null
            });

            // Check both actualRowCount and data array length since actualRowCount might be undefined
            // Also ensure actualRowCount is not 0 (which could indicate an error or no permission)
            return (response.actualRowCount > 0) || (response.data && response.data.length > 0);
        } catch (error) {
            log.error("Error checking driver advance history:", error);
            return false;
        }
    }
}
